import * as React from "react";
import { Field, FieldProps } from "../form/field";
import { useAsyncList, useComboBoxState } from "react-stately";
import {
  AriaComboBoxProps,
  mergeProps,
  useComboBox,
  useFilter,
} from "react-aria";
import { ListBox } from "../list-box";
import { InternalPopover } from "../internal-popover";
import { IconButton } from "../button";
import { useListBoxContent } from "../list-box/list-box.utils";
import { ButtonSize } from "../../utils/theme";
import { cx } from "../../utils/dom";
import { ListBoxRenderProps } from "../list-box/list-box";
import { Loader } from "../loader";

interface ComboBoxProps<T extends object>
  extends Omit<FieldProps<"input">, "children">,
    Omit<AriaComboBoxProps<T>, "children">,
    ListBoxRenderProps<T> {
  selectorIcon?: React.ReactNode;
  selectorSize?: ButtonSize;
  isLoading?: boolean;
  loadingIndicator?: React.ReactNode;
  loadData?: (filter?: string, signal?: AbortSignal) => Promise<T[]>;
}

export function ComboBox<T extends object>({
  description,
  descriptionProps,
  error,
  errorMessageProps,
  label,
  labelProps,
  selectorIcon,
  selectorSize = "sm",
  style,
  styles,
  className,
  classNames,
  isLoading,
  loadData,
  loadingIndicator = <Loader size="xs" />,
  ...props
}: ComboBoxProps<T>) {
  const list = useAsyncList({
    async load({ filterText, signal }) {
      if (loadData) {
        const res = await loadData(filterText, signal);
        return {
          items: res,
        };
      }

      return { items: [] };
    },
  });

  if (loadData) {
    props.items = list.items as T[];
    props.inputValue = list.filterText;
    props.onInputChange = list.setFilterText;
    isLoading = list.isLoading;
  }

  const { contains } = useFilter({ sensitivity: "base" });

  const children = useListBoxContent(props);
  const state = useComboBoxState({
    ...props,
    children: children,
    defaultFilter: contains,
  });

  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const listBoxRef = React.useRef<HTMLUListElement>(null);
  const popoverRef = React.useRef<HTMLDivElement>(null);
  const wrapperRef = React.useRef<HTMLDivElement>(null);

  const {
    inputProps,
    buttonProps,
    listBoxProps,
    labelProps: AriaLabelProps,
  } = useComboBox(
    {
      ...props,
      buttonRef,
      inputRef,
      listBoxRef,
      popoverRef,
    },
    state
  );

  return (
    <>
      <Field
        styles={styles}
        className={cx(className, "p-combo-box-field")}
        classNames={classNames}
        style={style}
        wrapperRef={wrapperRef}
        label={label}
        labelProps={mergeProps(labelProps, AriaLabelProps)}
        description={description}
        descriptionProps={descriptionProps}
        error={error}
        errorMessageProps={errorMessageProps}
      >
        <input className="p-text-input-el" ref={inputRef} {...inputProps} />
        <IconButton
          variant="ghost"
          color="gray"
          size={selectorSize}
          {...buttonProps}
        >
          {isLoading ? loadingIndicator : selectorIcon}
        </IconButton>
      </Field>
      {state.isOpen && (
        <InternalPopover
          offset={6}
          triggerRef={wrapperRef}
          state={state}
          popoverRef={popoverRef}
          isNonModal
        >
          <ListBox
            width={wrapperRef.current?.getBoundingClientRect()?.width}
            listBoxRef={listBoxRef}
            state={state}
            {...listBoxProps}
          ></ListBox>
        </InternalPopover>
      )}
    </>
  );
}
